import { isEmpty } from "lodash";
import DetectRTC from "detectrtc";
import reduxStore from "../../../configureEnvStore";
import { SCREEN_SHARE_AVAILABILITY_TYPES, BROWSER, SUPPORTED_BROWSER_VERSION } from "../../../constants/BottomMenuConstants";
import {
    setIsConfirmScreenShareModalOpen,
    setIsLocalUserScreenSharing,
    setIsScreenShareInfoOpen,
    setIsScreenShareMirrorWarningOpen,
    setScreenShareErrorOccurredFor,
    setScreenShareUnsupportedMsg,
} from "../../../reducers/twilioEvents-reducer";
import { dualStreamEnabled, generateInviteUrl, getExactBrowserName } from "../../../shared/utility";
import { LogFirebase } from "../../../services/firestore";
import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../../constants/PublicVariables";
import Localize from "../../../locale/Localize";
import {
    setAllowMarkup,
    setAllowPointer,
    setAllowTakePhoto,
    setIsAppDownloadModalOpen,
    setIsConfirmMultiStreamOpen,
} from "../../../reducers/common-reducer";
import { EVENT_NAME } from "../../../constants/GTagConstants";
import { STATUS } from "../../../constants/LivePointerConstants";
import googleEvent from "../../../services/googleEvent";
import { handleSendVideoScreenShareDataTrack, sendMsgForPointer, sendScreenshareStatusToAgent } from "../../../shared/dataTrackFunction";
import livePointerService from "../../LivePointer/livePointerService";
import { PARTICIPANT_TYPE } from "../../../constants/InviteConstants";
import navigatorService from "../../../services/navigatorService";
import { APP_URL_ANDROID, APP_URL_IOS, DEVICE_TYPES, DISABLE, ENABLED } from "../../../constants/Constants";
import { attachTrackInContainer, performStartStopVideo } from "../VideoCall/VideoUtils";
import { isSilentObserverAvailable } from "../../../shared/function";
import { isvideoenabled, isLocalUserStreaming, isParticipantStreaming, getUserDetailsById } from "../Room/roomFunction";
import { showGridView } from "../../GalleryView/GalleryViewUtils";
import { SCREEN_SHARE_DIMENSION, SCREENSHARE_FPS } from "../../../constants/CallSettingConstants";
import { getRoom, getScreenShareLocalTrack, setScreenShareLocalTrack } from "../Room/roomService";
import {
    createMyLocalScreenShareTrack,
    getActiveRoom,
    getPublishVideoContainer,
    getTracksByType,
    removeVideoElement,
    setTrackName,
    showMeRoomTypeIsGORoom,
    videoTracksExist,
} from "../twilioCallUtility";
import { updateStreamingStatusOnEvent } from "../videoScreenShareOverrideFunction";
import { setIsAppDownloadModalOpenForMeasurement } from "../../../reducers/measurement-reducer";
import { removeScreenshareNotificationModal } from "../../ParticipantsList/HostControlledParticipants/HostControlledParticipantUtils";

export const handleOnScreenShare = () => {
    // old callScreenShare function
    const { isLocalUserScreenSharing, startLocalVideo, screenSharingUsers, videoStreamingUsers } = reduxStore.getState().twilioEvents;
    const { expandView } = reduxStore.getState().commonReducer;

    const handleScreenSharingForSingleStream = () => {
        if (isEmpty(screenSharingUsers) && isEmpty(videoStreamingUsers)) {
            startScreenShare();
        } else if (!isLocalUserScreenSharing && (!isEmpty(videoStreamingUsers) || !isEmpty(screenSharingUsers))) {
            reduxStore.dispatch(setIsConfirmScreenShareModalOpen(true));
        } else if (isLocalUserScreenSharing) {
            stopScreenShare();
        }
    };

    const handleScreenSharingForDualStream = () => {
        if (startLocalVideo) {
            reduxStore.dispatch(setIsConfirmMultiStreamOpen(true));
        } else if (isLocalUserScreenSharing) {
            if (expandView && isLocalUserStreaming()) {
                LogFirebase("MultiStream", "ScreenShareOff", CALL_DATA.localIdentity);
                showGridView(CALL_DATA.localIdentity, true);
            }
            const previewContainer = getPublishVideoContainer();
            removeVideoElement(previewContainer);
            stopScreenShare();
        } else {
            startScreenShare();
        }
    };

    if (dualStreamEnabled()) {
        handleScreenSharingForDualStream();
    } else {
        handleScreenSharingForSingleStream();
    }
};

export const getScreenShareMediaConstraints = () => {
    return {
        //1920x1080 for screen share media stream
        video: {
            width: { max: SCREEN_SHARE_DIMENSION.width },
            height: { max: SCREEN_SHARE_DIMENSION.height },
        },
    };
};

export const handleOverrideOnScreenShareStart = () => {
    const { isOpponentUserScreenSharing, videoStreamingUsers, startLocalVideo, streamingPartiIdentity } = reduxStore.getState().twilioEvents;

    if (startLocalVideo) {
        LogFirebase("Screenshare", "local_video_on", "call unpublish video");
        LogFirebase("Video", "start_screen_share", "override local video");
        performStartStopVideo();
    } else if (isOpponentUserScreenSharing) {
        LogFirebase("Screenshare", "start", "override remote screen share");
    } else if (videoStreamingUsers.length > 0 && !videoStreamingUsers.includes(CALL_DATA.participantIdentity)) {
        // If Remote User is Streaming Video
        LogFirebase("Video", "start_screen_share", "override remote video " + streamingPartiIdentity);
    }
};

export const startScreenShare = async () => {
    // old function screenShare
    const { isLocalUserScreenSharing } = reduxStore.getState().twilioEvents;

    if (!isLocalUserScreenSharing) {
        const mediaConstraints = getScreenShareMediaConstraints();
        try {
            const mediaStream = await navigator.mediaDevices.getDisplayMedia(mediaConstraints);
            if (mediaStream) {
                LogFirebase("Screenshare", "screen_share_track_created", "Local");
                handleOverrideOnScreenShareStart();

                const params = { name: setTrackName("screenShare"), frameRate: SCREENSHARE_FPS };
                const screenLocalTrack = createMyLocalScreenShareTrack(mediaStream, params); // create twilio screen share track

                setScreenShareLocalTrack(screenLocalTrack); // set screen share local track in provider

                // listener for screen sharing stopped fron the bottom bar of extension which calls stopScreenShare function
                mediaStream?.getVideoTracks()[0]?.addEventListener("ended", () => handleOnScreenShare());

                const room = getRoom();
                room.localParticipant.publishTrack(screenLocalTrack); // track publish in room

                /*
                    To restrict Screen share Info div when screen sharing with "Tab" option 
                    If "Ignore" button not pressed then shows Informative UI else shows ongoing screen Info UI.
                */
                updateScreenShareUi(mediaStream);

                performOnLocalScreenShareStarted(); // on screen share start handle other functionality

                setTimeout(() => {
                    if (!dualStreamEnabled()) {
                        attachTrackInContainer(screenLocalTrack, "screenshare", 5);
                    }
                }, 100);
            } else {
                LogFirebase("Screenshare", "screen_share_track_creation_failed", { mediaStream: mediaStream });
            }
        } catch (error) {
            handleScreenShareError(error);
        }
    }
};

export const stopScreenShare = (notify, removeTracks) => {
    const { pointerOn } = reduxStore.getState().livePointer;
    const { isLocalUserScreenSharing } = reduxStore.getState().twilioEvents;
    const { isRemoteParticipantJoined, expandView } = reduxStore.getState().commonReducer;

    if (!isLocalUserScreenSharing) return;

    googleEvent.logEvent(EVENT_NAME.STOPPED_SCREEN_SHARE);
    LogFirebase("Screenshare", "stopped_screenshare", "");

    resetScreenShareUi();

    let removeLocalTracks = true;
    if (removeTracks != undefined && removeTracks == false) {
        removeLocalTracks = false;
    }

    if (dualStreamEnabled()) {
        if (!videoTracksExist()) {
            reduxStore.dispatch(setAllowTakePhoto(false));
        }
    } else {
        if (!isvideoenabled()) {
            reduxStore.dispatch(setAllowTakePhoto(false));
        }
    }
    sendScreenshareStatusToAgent(DISABLE);
    const screenShareLocalTrack = getScreenShareLocalTrack();
    const room = getRoom();
    room.localParticipant.unpublishTrack(screenShareLocalTrack);
    if (removeLocalTracks) {
        screenShareLocalTrack.stop();
        screenShareLocalTrack.detach().forEach(function (detachedElement) {
            detachedElement.remove();
        });
        setScreenShareLocalTrack(null);
    }

    const params = {
        isVideo: false,
        identity: CALL_DATA.localIdentity,
        isScreenShare: false,
        isLocalUser: true,
    };
    updateStreamingStatusOnEvent(params.isVideo, params.identity, params.isScreenShare, params.isLocalUser); // update streaming identity

    if (dualStreamEnabled()) {
        // handle case for when user clicks on browser's Stop screen share propmt clicks
        if (expandView && isLocalUserStreaming()) {
            showGridView(CALL_DATA.localIdentity, true); //safari bg/fg case
        }
    }

    if (notify != undefined && notify == false) {
        //do not send message when someone overwrites SS
        if (isRemoteParticipantJoined && pointerOn) {
            //turn off local live pointer and send message to participant to
            livePointerService.turnOffLivePointer();
        }
    } else {
        if (removeLocalTracks) handleSendVideoScreenShareDataTrack("0", "0", "1", (info) => LogFirebase("Screenshare", "send_msg_stop_ss", info));
        if (!dualStreamEnabled()) {
            if (GLOBAL_MEETING_DATA.isAgent) {
                if (pointerOn) {
                    livePointerService.turnOffLivePointer();
                    sendMsgForPointer(STATUS.DISABLED);
                } else {
                    if (!isvideoenabled()) {
                        sendMsgForPointer(STATUS.DISABLED);
                    }
                }
            } else {
                if (pointerOn) {
                    livePointerService.turnOffLivePointer();
                }
                // if web guest ss on and livepointer ON and stopscreenshare send data-track
                if (!isvideoenabled()) {
                    sendMsgForPointer(STATUS.DISABLED);
                }
            }
        }
    }
};

export const updateScreenShareUi = (mediaStream) => {
    reduxStore.dispatch(setIsLocalUserScreenSharing(true));
    const { isScreenShareMirrorWarningIgnored } = reduxStore.getState().twilioEvents;
    const isSharingWholeEntireScreen = mediaStream?.getTracks()[0]?.getSettings()?.displaySurface !== "browser";

    if (isSharingWholeEntireScreen && !isScreenShareMirrorWarningIgnored) {
        reduxStore.dispatch(setIsScreenShareMirrorWarningOpen(true));
    } else {
        reduxStore.dispatch(setIsScreenShareInfoOpen(true));
    }
};

export const resetScreenShareUi = () => {
    reduxStore.dispatch(setIsLocalUserScreenSharing(false));
    reduxStore.dispatch(setIsScreenShareMirrorWarningOpen(false));
    reduxStore.dispatch(setIsScreenShareInfoOpen(false));
};

export const handleScreenShareError = (error) => {
    // old function screenShare
    const OSName = DetectRTC.osName.toLowerCase();
    let { browserName } = getExactBrowserName();
    browserName = browserName.toLowerCase();

    const errorName = error?.name;
    const errorMessage = error?.message;

    LogFirebase("Screenshare", "stream_not_found_error", errorName);
    console.error("ScreenShare Error Catch: " + errorName);

    let errorOcurredFor = false;

    if (
        errorName == "NotAllowedError" &&
        errorMessage == "Permission denied by system" &&
        browserName == BROWSER.CHROME &&
        OSName.indexOf("mac") > -1
    ) {
        //only for mac chrome
        errorOcurredFor = BROWSER.MAC;
    } else if (errorName == "NotAllowedError" && browserName == BROWSER.SAFARI) {
        errorOcurredFor = BROWSER.MAC;
    } else if (errorName == "NotFoundError" && browserName == BROWSER.FIREFOX) {
        errorOcurredFor = BROWSER.MAC;
    } else if (errorName == "NotAllowedError" && browserName == BROWSER.FIREFOX) {
        errorOcurredFor = BROWSER.FIREFOX;
    } else {
        console.error("ScreenShare Error Catch-Else: " + error);
    }

    reduxStore.dispatch(setScreenShareErrorOccurredFor(errorOcurredFor));
    LogFirebase("Screenshare", "Error", { errorName: errorName, errorMessage: errorMessage });
};

// check screenShare feature available or not
export const checkScreenShareFeatureAvailable = () => {
    const SHOWMESCREENSHARE = PublicVariables.featureFlag?.SHOWMESCREENSHARE;
    return (
        !isSilentObserverAvailable() &&
        PublicVariables.featureFlag?.ENABLESCREENSHARE !== SCREEN_SHARE_AVAILABILITY_TYPES.offForAllParticipant &&
        (SHOWMESCREENSHARE === SCREEN_SHARE_AVAILABILITY_TYPES.availableForAllParticipant ||
            (GLOBAL_MEETING_DATA.isAgent && SHOWMESCREENSHARE === SCREEN_SHARE_AVAILABILITY_TYPES.availableForHost) ||
            (GLOBAL_MEETING_DATA.isGuest && SHOWMESCREENSHARE === SCREEN_SHARE_AVAILABILITY_TYPES.availableForGuest))
    );
};

// Check if the current browser supports screen sharing
export const isBrowserSupportScreenShare = function () {
    let { browserName, browserVersion } = getExactBrowserName();
    browserName = browserName.toLowerCase();
    browserVersion = parseFloat(browserVersion);
    const OSName = DetectRTC.osName.toLowerCase();

    let unSupportedReason = "";
    let supported = false;

    if (browserName == BROWSER.CHROME && browserVersion >= 75) {
        supported = true;
    } else if (browserName == BROWSER.FIREFOX && browserVersion >= 91) {
        supported = true;
    } else if (browserName == BROWSER.EDGE && browserVersion >= 75) {
        supported = true;
    } else if (browserName == BROWSER.SAFARI && OSName === "mac os x" && browserVersion >= SUPPORTED_BROWSER_VERSION.SAFARI) {
        // Safari on macOS supports getDisplayMedia since version 13
        // Note: Safari on iOS does NOT support screen sharing
        supported = true;
    } else if (browserName === BROWSER.CHROME || browserName === BROWSER.FIREFOX || browserName === BROWSER.EDGE || browserName === BROWSER.SAFARI) {
        //lower version browsers
        unSupportedReason = Localize[PublicVariables.currentLanguageObj.languageCode]["update_your_browser_to_share_your_screen"];
    } else {
        //browser doesn't support ss api at all.
        unSupportedReason = Localize[PublicVariables.currentLanguageObj.languageCode]["use_chrome_to_share_your_screen"];
    }

    reduxStore.dispatch(setScreenShareUnsupportedMsg(unSupportedReason));

    return supported;
};

// performs when local screen share started
export const performOnLocalScreenShareStarted = () => {
    // performOnScreenPublish, screenShare old function
    const { pointerOn } = reduxStore.getState().livePointer;
    CALL_DATA.disabledFlash = true;
    googleEvent.logEvent(EVENT_NAME.STARTED_SCREEN_SHARE);
    LogFirebase("Screenshare", "started_screen_share", "");
    removeScreenshareNotificationModal();
    sendScreenshareStatusToAgent(ENABLED);
    if (!dualStreamEnabled()) {
        const params = {
            isVideo: false,
            identity: CALL_DATA.localIdentity,
            isScreenShare: true,
            isLocalUser: true,
        };
        updateStreamingStatusOnEvent(params.isVideo, params.identity, params.isScreenShare, params.isLocalUser); // update streaming identity
    }

    handleSendVideoScreenShareDataTrack("1", "0", "1", (info) => LogFirebase("Screenshare", "send_msg_start_ss", info));

    reduxStore.dispatch(setIsLocalUserScreenSharing(true));
    reduxStore.dispatch(setAllowMarkup(true));
    reduxStore.dispatch(setAllowTakePhoto(true));
    reduxStore.dispatch(setAllowPointer(true));

    if (!dualStreamEnabled()) {
        sendMsgForPointer(STATUS.ENABLED);
        if (pointerOn) {
            sendMsgForPointer(STATUS.OFF); // for single strem if override the ss with video
            livePointerService.turnOffLivePointer();
        }
        // Auto-start live pointer after local screenshare starts - pass track for proper Twilio event handling
        const screenShareTrack = getScreenShareLocalTrack();
        livePointerService.autoStartLivePointer("local_screenshare", screenShareTrack);
    }
};

export const isScreenShareAvailable = () => {
    let isScreenShare = false;
    const streamingPartiIdentity = reduxStore.getState().twilioEvents?.streamingPartiIdentity;
    const userDetails = getUserDetailsById(streamingPartiIdentity);

    if (userDetails) {
        const deviceType = userDetails.device_type;
        if (deviceType == DEVICE_TYPES.DESKTOP || deviceType == DEVICE_TYPES.ANDROID_APP || deviceType == DEVICE_TYPES.IOS_APP) {
            const room = getActiveRoom();
            if (!room) return false;
            // Check remote participants
            room?.participants?.forEach((remoteParticipant) => {
                if (!isScreenShare && isParticipantStreaming(remoteParticipant.identity)) {
                    const trackArr = getTracksByType(remoteParticipant, "video");
                    trackArr.forEach((track) => {
                        if (track.kind === "video") {
                            if (isScreenshareTrack(track)) {
                                isScreenShare = true;
                            } else {
                                isScreenShare = false;
                            }
                        }
                    });
                }
            });

            // Check local participant (guest user case)
            room?.localParticipant?.videoTracks?.forEach(({ track }) => {
                if (!isScreenShare && isParticipantStreaming(room.localParticipant.identity)) {
                    if (track.kind === "video" && isScreenshareTrack(track)) {
                        isScreenShare = true;
                    }
                }
            });
        }
    }

    return isScreenShare;
};

export const openStore = () => {
    if (navigatorService.isAndroid()) {
        window.location.href = APP_URL_ANDROID;
        LogFirebase("MwebScreenshare ", "redirect_to", "Play store");
    } else {
        window.location.href = APP_URL_IOS;
        LogFirebase("MwebScreenshare ", "redirect_to", "App store");
    }
    googleEvent.logEvent(EVENT_NAME.MWEB_SCREENSHARE_REDIRECT_TO_STORE);
};

export const disconnectAndLaunchApp = (from) => {
    const guestName = CALL_DATA.participantUserName.replace("&", " ");
    const guestlink = generateInviteUrl(PARTICIPANT_TYPE.GUEST);
    const currentLanguageCode = PublicVariables.currentLanguageObj.languageCode;
    const { startLocalVideo } = reduxStore.getState().twilioEvents;

    let guestLinkWithDeepLinking = "";

    if (from == "screenshare") {
        guestLinkWithDeepLinking = guestlink + "&l=" + currentLanguageCode + "&pid=" + CALL_DATA.localIdentity + "&n=" + guestName;
        reduxStore.dispatch(setIsAppDownloadModalOpen(false));
    } else if (from == "measurement") {
        guestLinkWithDeepLinking = guestlink + "&l=" + currentLanguageCode + "&ar=" + CALL_DATA.localIdentity + "&n=" + guestName;
        reduxStore.dispatch(setIsAppDownloadModalOpenForMeasurement(false));
    }

    //need to stop else mobile app will have override pop up
    if (startLocalVideo) {
        performStartStopVideo();
    }

    if (guestLinkWithDeepLinking) {
        if (navigatorService.isIosDevice()) {
            /**
             * using schema URL open app using native popup
             * Schema URL open if app is install, other vise safari default error popup will show to user
             **/
            let iOSAppDeepLinkURL = "";

            if (from == "screenshare") {
                iOSAppDeepLinkURL =
                    "blitzzvideo://joincall/?&l=" +
                    currentLanguageCode +
                    "&pid=" +
                    CALL_DATA.localIdentity +
                    "&m=" +
                    PublicVariables.featureFlag.SHOWMEMICROPHONE +
                    "&s=" +
                    PublicVariables.featureFlag.SMSSUPPORTOPTION +
                    "&code=" +
                    GLOBAL_MEETING_DATA.sessionId +
                    "&n=" +
                    guestName;
            } else if (from == "measurement") {
                iOSAppDeepLinkURL =
                    "blitzzvideo://joincall/?&l=" +
                    currentLanguageCode +
                    "&m=" +
                    PublicVariables.featureFlag.SHOWMEMICROPHONE +
                    "&s=" +
                    PublicVariables.featureFlag.SMSSUPPORTOPTION +
                    "&code=" +
                    GLOBAL_MEETING_DATA.sessionId +
                    "&ar=" +
                    CALL_DATA.localIdentity +
                    "&n=" +
                    guestName;
            }

            window.location = iOSAppDeepLinkURL;
        } else {
            window.open(guestLinkWithDeepLinking);
        }
        if (from == "screenshare") {
            LogFirebase("MwebScreenshare", "redirect_to", "Launch App");
            googleEvent.logEvent(EVENT_NAME.MWEB_SCREENSHARE_DISCONNECT_AND_LAUNCH_APP);
        } else if (from == "measurement") {
            LogFirebase("MwebMeasurement", "redirect_to", "Launch App");
            googleEvent.logEvent(EVENT_NAME.MWEB_MEASUREMENT_DISCONNECT_AND_LAUNCH_APP);
        }
    }
};

export const handleClickOnStartScreenShare = () => {
    if (!PublicVariables.isMobile) {
        handleOnScreenShare();
    } else {
        reduxStore.dispatch(setIsAppDownloadModalOpen(true));
        LogFirebase("MwebScreenshare ", "popup", "Opened");
        googleEvent.logEvent(EVENT_NAME.MWEB_SCREENSHARE_POPUP_OPNED);
    }
};

export const isScreenshareTrack = (track) => {
    if (!track?.name) return false;

    return track.name.charAt(0) === "s";
};

export const buttonNotShowOnGORoom = () => {
    return GLOBAL_MEETING_DATA.isGuest && PublicVariables.isMobile && showMeRoomTypeIsGORoom(PublicVariables.featureFlag);
};
